import React, { useState, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import InfoDialog from '../dialogs/InfoDialog';
import TR from './../../Trans';
import ExternalViewCommands from './../../services/external';
import { checkIfMobile } from '../../services/screen';
import muvikaAttrs from './../../attrs';
import { ReactComponent as MuvikaLogo } from './../../assets/logo-horiz.svg';
import { ReactComponent as MuvikaLogoPro } from './../../assets/logo-horiz-pro.svg';
import { ReactComponent as MuvikaLogoInv } from './../../assets/logo-horiz-inv.svg';
import { ReactComponent as MuvikaLogoProInv } from './../../assets/logo-horiz-pro-inv.svg';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Toolbar from '@mui/material/Toolbar';
import IntervalService from './../../services/intervals';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import FormControlLabel from '@mui/material/FormControlLabel';
import CloseIcon from '@mui/icons-material/Close';
import DeleteDialog from './../dialogs/DeleteDialog';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import NewAccountDialog from '../dialogs/NewAccountDialog';
import SearchField from './../items/SearchField';
import ConfirmPlayerExit from '../dialogs/ConfirmPlayerExit';
import { Capacitor } from '@capacitor/core';
import { getOS } from './../../services/os';
import AndroidSubDialog from '../dialogs/AndroidSubDialog';
import LogoutFeedbackDialog from '../dialogs/LogoutFeedbackDialog';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { Views } from './../../enums/enums';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import sessionStore from './../../stores/session';
import searchStore from './../../stores/search';
import messageStore from './../../stores/message';
import settingsStore from '../../stores/settings';
import performanceStore from './../../stores/performance';
import accountStore from '../../stores/account';
import MuvikaService from './../../services/muvika';
import playerStore from './../../stores/player';
import navigationStore from '../../stores/navigation';
import singersStore from './../../stores/singers';
import EditPerformance from '../dialogs/EditPerformance';
import newTracksStore from './../../stores/newTracks';
import favoritesStore from '../../stores/favorites';
import catalogStore from '../../stores/catalog';
import environmentStore from '../../stores/environment';
import musicStore from '../../stores/music';
import musicPlayerStore from '../../stores/musicPlayer';
import newsStore from '../../stores/news';
import songListStore from '../../stores/songList';
import timeoutsStore from '../../stores/timeouts';
import statusStore from './../../stores/status';
import homeStore from '../../stores/home';
import SubscribeDialog from '../dialogs/SubscriptionDialog';
import UpdateDialog from './../dialogs/UpdateDialog';
import MessageDialog from './../dialogs/MessageDialog';
import { AndroidExternalScreen } from 'muvika-android-external-screen';
import bgFiltersStore from '../../stores/bgFilters';
import bgStylesStore from '../../stores/bgStyles';
import bgPerformanceStore from '../../stores/bgPerformance';
import bgSearchStore from '../../stores/bgSearch';
import bgPlaylistsStore from './../../stores/bgPlaylists';
import PinQuestion from '../dialogs/PinQuestion';
import IosExternalLinkDialog from '../dialogs/IosExternalLinkDialog';
import { useLocalStorage } from '../../services/useLocalStorage';
import songbookQueueStore from '../../stores/songbookQueue';

function resetStores(isWaitingLoggedIn = false) {
  accountStore.getState().reset();
  catalogStore.getState().reset();
  environmentStore.getState().reset();
  favoritesStore.getState().reset();
  messageStore.getState().reset();
  musicPlayerStore.getState().reset();
  musicStore.getState().reset();
  navigationStore.getState().reset();
  newsStore.getState().reset();
  homeStore.getState().reset();
  newTracksStore.getState().reset();
  performanceStore.getState().reset();
  playerStore.getState().reset();
  searchStore.getState().reset();
  sessionStore.setState({
    isSessionOpening: false,
    isThrownOut: false,
    isLoggedIn: false,
    isAckSmartQ: false,
    externalWindowHandler: null,
    isLoginSeqErrors: false,
    isLoginSeqError: false,
    isWaiting: false,
    requestCount: 0,
    isWaitingLoggedIn: isWaitingLoggedIn,
  });
  settingsStore.getState().reset();
  singersStore.getState().reset();
  songListStore.getState().reset();
  timeoutsStore.getState().reset();
  IntervalService.clearAllIntervals();
  IntervalService.clearSongbookInterval();
  IntervalService.clearNewTracksInterval();
  IntervalService.clearSubInterval();
  IntervalService.clearAllStreamUrlTimeouts();
  IntervalService.clearStatusReportInterval();
  bgFiltersStore.getState().reset();
  bgStylesStore.getState().reset();
  bgPerformanceStore.getState().reset();
  bgSearchStore.getState().reset();
  bgPlaylistsStore.getState().reset();
  musicPlayerStore.getState().reset();

}


function NavBar() {

  const itemsOnPage = 25;
  const sessionToken = sessionStore((state) => state.sessionToken);
  const safeArea = navigationStore((state) => state.safeArea);
  const logout = sessionStore((state) => state.logout);
  const search = searchStore((state) => state.search);
  const goToView = navigationStore((state) => state.goToView);
  const emptyPlayer = playerStore((state) => state.empty);
  const setIsStopped = playerStore((state) => state.setIsStopped);
  const searchQuery = searchStore((state) => state.searchQuery);
  const setSearchQuery = searchStore((state) => state.setSearchQuery);
  const setError = messageStore((state) => state.setError);
  const settings = settingsStore((state) => state.settings);
  const getSettings = settingsStore((state) => state.getSettings);
  const setSettings = settingsStore((state) => state.setSettings);
  const clearHistory = performanceStore((state) => state.clearHistory);
  const clearPlayQueue = performanceStore((state) => state.clearPlayQueue);
  const account = accountStore((state) => state.account);
  const getAccount = accountStore((state) => state.getAccount);
  const session = sessionStore((state) => state.session);
  const urls = accountStore((state) => state.urls);
  const isLogoutFeedbackDialogShown = navigationStore((state) => state.isLogoutFeedbackDialogShown);
  const setLogoutFeedbackDialogShown = navigationStore((state) => state.setLogoutFeedbackDialogShown);
  const openSessionAndOneTimeLogin = sessionStore((state) => state.openSessionAndOneTimeLogin);
  const isLoggedAs = sessionStore((state) => state.isLoggedAs);
  const toggleFieldTitle = searchStore((state) => state.toggleFieldTitle);
  const toggleFieldArtist = searchStore((state) => state.toggleFieldArtist);
  const timeDiff = accountStore((state) => state.timeDiff);
  const searchNavBarInputRef = searchStore((state) => state.searchNavBarInputRef);
  const clearFilters = searchStore((state) => state.clearFilters);
  const setResetArtistSearchMode = searchStore((state) => state.setResetArtistSearchMode);
  const isArtistSearchMode = searchStore((state) => state.isArtistSearchMode);
  const logoutChange = sessionStore((state) => state.logoutChange);

  const [shopUrl, setShopUrl] = useState(null);
  const [isConfirmPlayerExitDialogOpen, setIsConfirmPlayerExitDialogOpen] = useState(false);

  const handleOpenConfirmPlayerExitDialog = () => {
    setIsConfirmPlayerExitDialogOpen(true);
  };

  const handleCloseConfirmPlayerExitDialog = () => {
    setIsConfirmPlayerExitDialogOpen(false);
  };

  const isMobile = useMediaQuery({ query: '(max-width: 720px)' });

  const handleAndroidSubDialogOk = () => {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
      ExternalViewCommands.loadBasicToAndroidExternalScreen();
    } else if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      ExternalViewCommands.loadBasicToIosExternalScreen();
    }
    logoutChange(account.username, androidSubOneTimeLoginCode);
    setAndroidSubOneTimeLoginCode(null);
    setIsAndroidSubDialogOpen(false);
  };

  const [isAndroidSubDialogOpen, setIsAndroidSubDialogOpen] = useState(false);
  const [androidSubOneTimeLoginCode, setAndroidSubOneTimeLoginCode] = useState(null);

  const handleOpenAndroidSubDialog = () => {
    MuvikaService.createAccountLink(sessionToken, account.username, "one_time_login")
      .then(function (res) {
        //console.log(res);
        if (res.data.error) {

        } else {
          setAndroidSubOneTimeLoginCode(res.data.data.code);
          setIsAndroidSubDialogOpen(true);
        }
      })
      .catch(function () { });
  };

  const handleCloseAndroidSubDialog = () => {
    setIsAndroidSubDialogOpen(false);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [isAccountMenuOpen, setAccountMenuOpen] = useState(false);
  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false);

  const [isSongbookChecked, setIsSongbookChecked] = useState(false);

  const [isPinQuestionOpen, setIsPinQuestionOpen] = useState(false);

  const [isNewAccountDialogOpen, setIsNewAccountDialogOpen] = useState(false);

  const [isIosExternalLinkDialogOpen, setIsIosExternalLinkDialogOpen] = useState(false);

  const handleOpenIosExternalLinkDialog = () => {
    setIsIosExternalLinkDialogOpen(true);
  };

  const handleCloseIosExternalLinkDialog = () => {
    setIsIosExternalLinkDialogOpen(false);
  };

  const [isLogOutFeedbackDialogOpen, setIsLogOutFeedbackDialogOpen] = useState(false);

  const logoutNow = () => {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
      ExternalViewCommands.loadBasicToAndroidExternalScreen();
    } else if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      ExternalViewCommands.loadBasicToIosExternalScreen();
    }
    logout();
  };

  const handleLogOutFeedback = () => {
    if (!isLogoutFeedbackDialogShown) {
      handleAccountMenuClose();
      setIsLogOutFeedbackDialogOpen(true);
    } else {
      setLogoutFeedbackDialogShown(false);
      logoutNow();
    }
  };

  const handleCloseLogOutFeedbackDialog = () => {
    setIsLogOutFeedbackDialogOpen(false);
  };

  const handleOpenNewAccountDialog = () => {
    setIsNewAccountDialogOpen(true);
  };

  const handleCloseNewAccountDialog = () => {
    setIsNewAccountDialogOpen(false);
  };

  useEffect(() => {
    if (!settings) {
      getSettings(sessionToken);
    }
    if (!account) {
      getAccount(sessionToken);
    }
  }, []);

  useEffect(() => {
    if (settings) {
      setIsSongbookChecked(settings.sb_enabled == 1);
    }
  }, [settings]);

  const handleClosePinQuestion = () => {
    setIsPinQuestionOpen(false);
    goToView(Views.Home);
  };

  const handleCheckPinToLogOut = (pin) => {
    if (settings.pin) {
      if (pin === settings.pin) {
        logoutNow();
      } else {
        setError(sessionToken, 0, TR.cm_invalid_pin);
      }
    } else {
      logoutNow();
    }
  };

  const [isSongbookInfoDialogOpen, setIsSongbookInfoDialogOpen] = useState(false);

  const handleOpenSongbookInfoDialog = (e) => {
    e.preventDefault();
    setIsSongbookInfoDialogOpen(true);
  };

  const handleCloseSongbookInfoDialog = () => {
    setIsSongbookInfoDialogOpen(false);
  };

  const handleEnableSongbook = (event) => {
    let newSettings = { ...settings };
    newSettings.sb_enabled = event.target.checked;
    setSettings(sessionToken, newSettings);
  };

  const handleOpenClearDialog = () => {
    setIsClearDialogOpen(true);
  };

  const handleCloseClearDialog = () => {
    setIsClearDialogOpen(false);
  };

  const handleAccountMenuClose = () => {
    setAccountMenuOpen(false);
  };

  const openAccountMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setAccountMenuOpen(true);
  };

  let [changedSearchQuery, setChangedSearchQuery] = useState(searchQuery);

  const handleSearchQueryChange = (e) => {
    setChangedSearchQuery(e.target.value);
    if (isArtistSearchMode) {
      setResetArtistSearchMode();
    }
  };

  const onSearch = () => {
    if (changedSearchQuery.length > 1) {
      setSearchQuery(changedSearchQuery);
      search(sessionToken, itemsOnPage);
      goToView(Views.Search);
      if (document.activeElement) {
        document.activeElement.blur();
      }
    } else {
      setError(sessionToken, 0, TR.cm_seach_text_too_short);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  const handleSearch = (event) => {
    onSearch();
  };

  const onClearing = () => {
    IntervalService.clearAllIntervals();
    setIsStopped(true);
    emptyPlayer();
    clearPlayQueue(sessionToken);
    clearHistory(sessionToken);
    handleCloseClearDialog();
  };

  const getPlainSongbookUrl = () => {
    if (urls) {
      let url = urls.url_songbook;
      let urlSplitted = url.split(".");
      if (urlSplitted.length > 2) {
        return urlSplitted[urlSplitted.length - 2] + "." + urlSplitted[urlSplitted.length - 1];
      } else {
        let urlSplitted = url.split("://");
        if (urlSplitted.length == 2) {
          return urlSplitted[1];
        }
      }
    }
    return "";
  };

  const renderSongbookCheckbox = () => {
    if (!checkIfMobile()) {
      if (settings) {
        if (settings.sb_enabled == 1) {
          return <FormControlLabel
            className="muvika-navbar-songbook-info"
            onChange={handleEnableSongbook}
            control={<Checkbox checked={isSongbookChecked} />}
            label={
              <>
                <span className="muvika-songbook-url">
                  {getPlainSongbookUrl()}{" "}
                </span>
                <span
                  className="muvika-songbook-code"
                  onClick={handleOpenSongbookInfoDialog}
                >
                  {settings.sb_code}
                </span>
              </>
            }
          />;
        } else {
          return <FormControlLabel className="muvika-navbar-songbook-info" onChange={handleEnableSongbook} control={<Checkbox checked={isSongbookChecked} />} label={<><span className="muvika-songbook-url">{TR.cm_enable_songbook}</span></>} />;
        }
      }
    }
  };

  const renderSongbookCode = () => {
    if (!checkIfMobile()) {
      if (settings) {
        if (settings.sb_enabled == 1) {
          return <span className="muvika-songbook-code" onClick={handleOpenSongbookInfoDialog}>{settings.sb_code}</span>;
        }
      }
    }
  };

  const goToShop = (sessionToken, accountId, email, url) => {
    MuvikaService.createAccountLink(sessionToken, email, "one_time_login")
      .then(function (res) {
        //console.log(res);
        if (res.data.error) {

        } else {
          if (Capacitor.isNativePlatform()) {
            window.open(url + "?origin=muvikaapp&id=" + accountId + "&login_code=" + res.data.data.code, "_self");
          } else {
            window.open(url + "?origin=muvikaweb&id=" + accountId + "&login_code=" + res.data.data.code, "_self");
          }
        }
      })
      .catch(function () { });
  };

  const isCustomer = (tags) => {
    if (tags) {
      return tags.includes("consumer");
    }
    return false;
  };

  const handleSubscription = () => {
    if (account && session) {
      if (isCustomer(account.tags)) {
        goToShop(sessionToken,
          account.account_id,
          account.username,
          session.urls.url_subscribe
        );
      } else {
        setShopUrl(session.urls.url_subscribe);
        handleOpenConfirmPlayerExitDialog();
      }
    }
  };

  const checkIfBeforeNow = (dateStr) => {
    var now = new Date();
    var date = new Date(dateStr);
    now.setMinutes(now.getMinutes() + Math.round(timeDiff));
    return date < now;
  };

  const handleGoToFeedback = () => {
    handleAccountMenuClose();
    handleCloseLogOutFeedbackDialog();
    setLogoutFeedbackDialogShown(true);
    goToView(Views.Feedback);
  };

  const handleLogOut = () => {
    handleAccountMenuClose();
    handleCloseLogOutFeedbackDialog();
    logoutNow();
  };

  const renderSubscribeNow = () => {
    if (settings) {
      if (!settings.account_pin) {
        if (account) {
          if (!settings.account_pin && account.subscription.valid == 0 && account.subscription.order_info.actions.includes("subscribe")) {
            if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
              return <Button className="muvika-navbar-subscribe-button" variant="text" color="success" onClick={handleOpenAndroidSubDialog}>{TR.cm_subscribe_now}</Button>;
            } else if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
              return <Button className="muvika-navbar-subscribe-button" variant="text" color="success" onClick={handleOpenIosExternalLinkDialog}>{TR.cm_subscribe_now}</Button>;
            } else {
              return <Button className="muvika-navbar-subscribe-button" variant="text" color="success" onClick={() => handleSubscription()}>{TR.cm_subscribe_now}</Button>;
            }
          }
        }
      }
    }
  };

  const renderOfficeName = () => {
    if (account && settings) {
      if (isLoggedAs) {
        if (account.office_name) {
          if (account.office_name.length > 0) {
            return <Tooltip title={account.office_name}>
              <span className="muvika-navbar-text muvika-logged-as">
                {account.office_name}
              </span>
            </Tooltip>;
          }
        }
        return <Tooltip title={account.firstname}>
          <span className="muvika-navbar-text muvika-logged-as" >
            {account.firstname}
          </span>
        </Tooltip>;
      } else {
        if (account.office_name) {
          if (account.office_name.length > 0) {
            return <Tooltip title={account.office_name}>
              <span className="muvika-navbar-text" >
                {account.office_name}
              </span>
            </Tooltip>;
          }
        }
        return <Tooltip title={account.firstname}>
          <span className="muvika-navbar-text" >
            {account.firstname}
          </span>
        </Tooltip>;
      }
    }
  };

  const renderBoothCleanButton = () => {
    if (settings) {
      if (settings.usage_mode === "booth") {
        return <div>
          <DeleteDialog
            open={isClearDialogOpen}
            onClose={handleCloseClearDialog}
            onDeletion={onClearing}
            title={TR.cm_clear_booth_session}
            cancel={TR.cm_cancel}
            delete={TR.cm_clear}
          />
          <Tooltip>

            <Box>
              <IconButton
                className="muvika-navbar-icon"
                size="large"
                edge="end"
                color="inherit"
                component="label"
                onClick={handleOpenClearDialog}
              >
                <CleaningServicesIcon />
              </IconButton>
            </Box>
          </Tooltip>
        </div>;
      }
    }
  };

  const renderPinQuestionDialog = () => {
    if (settings) {
      if (settings.usage_mode === "booth" && settings.pin) {
        return <PinQuestion open={isPinQuestionOpen} onClose={handleClosePinQuestion} onCheckPin={handleCheckPinToLogOut} />;
      }
    }
  };

  const renderLogOutButton = () => {
    if (settings) {
      if (settings.usage_mode === "booth") {
        return <Tooltip>
          <Box>
            <IconButton
              className="muvika-navbar-icon"
              size="large"
              edge="end"
              color="inherit"
              component="label"
              onClick={() => {
                if (settings.pin) {
                  setIsPinQuestionOpen(true);
                } else {
                  handleLogOutFeedback();
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Tooltip>;
      }
    }
  };

  const renderAccountButtonAndMenu = () => {
    if (settings) {
      if (!settings.usage_mode || !settings.usage_mode || settings.usage_mode === "restaurant") {
        return <Tooltip>
          <Box>
            <div className="muvika-navbar-account-button" onClick={openAccountMenu}>
              <AccountCircle />
            </div>
            <Menu disableAutoFocus disableEnforceFocus disableRestoreFocus
              id={"menu-account"}
              style={{ zIndex: 99999 }}
              open={isAccountMenuOpen}
              anchorEl={anchorEl}
              onClose={handleAccountMenuClose}
              MenuListProps={{
                'aria-labelledby': "menu-account",
              }}
            >
              <MenuItem onClick={() => {
                handleAccountMenuClose();
                goToView(Views.Account);
              }}>
                {TR.ww_your_account_information}
              </MenuItem>
              <MenuItem onClick={() => {
                handleAccountMenuClose();
                goToView(Views.ChangePassword);
              }}>
                {TR.cm_change_password}
              </MenuItem>
              <MenuItem onClick={() => {
                handleAccountMenuClose();
                goToView(Views.ChangeUsername);
              }}>
                {TR.cm_change_username}
              </MenuItem>
              <MenuItem onClick={() => logoutNow()}>
                {TR.cm_logout}
              </MenuItem>
            </Menu>
          </Box>
        </Tooltip>;
      }
    }
  };

  const [theme, setTheme] = useLocalStorage(muvikaAttrs.service + "-theme", "dark");

  const renderLogo = () => {
    if (account && settings) {
      if (account.tags.includes("consumer")) {
        if (!settings.theme || settings.theme === "dark") {
          return <MuvikaLogo className="muvika-logo-svg" />;
        } else {
          return <MuvikaLogoInv className="muvika-logo-svg" />;
        }
      } else {
        if (!settings.theme || settings.theme === "dark") {
          return <MuvikaLogoPro className="muvika-logo-svg" />;
        } else {
          return <MuvikaLogoProInv className="muvika-logo-svg" />;
        }
      }
    }
  };

  const handleSearchClear = () => {

  };

  const renderSeachBar = () => {
    if (!checkIfMobile()) {
      return <SearchField id="muvika-search-navbar" onref={searchNavBarInputRef} isNavBar={true} className="muvika-app-bar-search-field" value={searchQuery} onClear={handleSearchClear} onSearchQueryChange={handleSearchQueryChange} onSearch={handleSearch} onKeyDown={handleKeyDown} />;
    }
  };

  const renderCenterBar = () => {
    if (!checkIfMobile()) {
      return <Box className="muvika-navbar-left">
        <Stack direction="row" spacing={2} >
          {renderSeachBar()}
          {renderSongbookCheckbox()}
        </Stack>
      </Box>;
    }
  };

  return (
    <AppBar
      position="fixed"
      className="muvika-app-bar"
      sx={{ background: "var(--back-color) !important", paddingTop: safeArea ? safeArea.top + "px !important" : 0, paddingLeft: safeArea ? safeArea.left + "px !important" : 0, paddingRight: safeArea ? safeArea.right + "px !important" : 0 }}
    >
      <ConfirmPlayerExit
        open={isConfirmPlayerExitDialogOpen}
        onClose={handleCloseConfirmPlayerExitDialog}
        onConfirmation={() => {
          goToShop(sessionToken,
            account?.account_id,
            account?.username,
            shopUrl
          );
        }}
      />
      <LogoutFeedbackDialog
        open={isLogOutFeedbackDialogOpen}
        onClose={handleCloseLogOutFeedbackDialog}
        onYes={() => handleGoToFeedback()}
        onNo={() => handleLogOut()}
      />
      <IosExternalLinkDialog
        open={isIosExternalLinkDialogOpen}
        onClose={handleCloseIosExternalLinkDialog}
        message={TR.cm_external_link_account_ios}
        url={"https://muvika.com"}
      />
      <AndroidSubDialog
        open={isAndroidSubDialogOpen}
        onClose={handleCloseAndroidSubDialog}
        onOk={handleAndroidSubDialogOk}
      />
      <InfoDialog open={isSongbookInfoDialogOpen} onClose={handleCloseSongbookInfoDialog} title={TR.cm_songbook} text={TR.cm_songbook_info} />
      {renderPinQuestionDialog()}
      <Toolbar>
        <Box className="muvika-app-bar-logo-box">
          {renderLogo()}
        </Box>
        {renderCenterBar()}
        <Box className="muvika-navbar-right">
          <Stack direction="row" spacing={1} justifyContent="right">
            {renderSubscribeNow()}
            {renderOfficeName()}
            {renderBoothCleanButton()}
            {renderLogOutButton()}
            {renderAccountButtonAndMenu()}
          </Stack>
        </Box>
      </Toolbar>
    </AppBar >
  );
}

export default NavBar;
